import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {
	Box,
	Container,
	FormControl,
	Grid,
	Icon,
	MenuItem,
	MenuProps,
	OutlinedInput,
	Select,
	SelectChangeEvent,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useState } from 'react';

// Complex sub-components — shimmed from npm since this app only uses withScheduling=true (withSimpleLayout)
import BreadCrumbs from '@shared-components-web/components/BreadCrumbs';
import ContentfulButton from '@shared-components-web/components/ContentfulButton';
import {
	backToTopBoxWrapperStyles,
	backToTopLinkWrapperStyle,
	breadCrumbsAndTopBtnContainerStyles,
	breadCrumbsContainerStyles,
	copyrightStyles,
	getAboutWrapperStyles,
	getContactInfoWrapperStyles,
	getContentWrapperStyles,
	getReasonSelectStyles,
	getWrapperStyles,
	reasonMenuItemStyles,
	reasonSelectedMenuItemStyles,
	stickyDesktopFooterBtnsWrapperStyles,
	stickyDesktopFooterWrapperStyles,
	stickyMobileFooterWrapperStyles,
} from '@shared-components-web/components/Footer/Footer.styles';
import FooterNavigationContent from '@shared-components-web/components/Footer/FooterNavigationContent/FooterNavigationContent';
import FooterPolicy from '@shared-components-web/components/Footer/FooterPolicy';
import RichText, { RichTextContent } from '@shared-components-web/components/RichText/RichText';
import TAGLink from '@shared-components-web/components/TAGLink';
import TAGSvgIcon from '@shared-components-web/components/TAGSvgIcon/TAGSvgIcon';
import { REASONS_FOR_VISIT, SCHEDULE_PATH } from '@shared-components-web/constants';
import { AnalyticsContext, Maybe } from '@shared-components-web/types';
import { AdFooter, AdFooterStickyFooterButtonsCollection, Button, Page } from '@src/lib/__generated/contentful/sdk';

interface IStickyFooterButtons {
	stickyFooterButtonsCollection?: Maybe<AdFooterStickyFooterButtonsCollection>;
}
interface IStickyFooter extends IStickyFooterButtons {
	stickyFooterScheduleButton?: Maybe<Button>;
}

export interface IFooter {
	/**
	 * Application footer data, containing the information about the brand and the navigation links
	 *
	 * @type {AdFooter}
	 * @memberof IFooter
	 */
	footerData: AdFooter;
	/**
	 * Whether the footer should display the scheduling content or not
	 *
	 * @type {boolean}
	 * @memberof IFooter
	 */
	withScheduling?: boolean;
	/**
	 * breadcrumbsData to display breadcrumbs
	 *
	 * @type {boolean}
	 * @memberof IFooter
	 */
	breadcrumbsData?: Page;
	/**
	 * Sets the Footer background color to light if true, otherwise dark.
	 *
	 * @type {boolean}
	 * @memberof IFooter
	 */
	isLightFooter?: boolean;
	/**
	 * Whether the sticky footer on mobile should be hidden for a particular page.
	 *
	 * @type {boolean | null}
	 * @memberof IFooter
	 */
	disableStickyFooterOnMobile?: boolean | null;
	/**
	 * Whether the sticky footer on desktop should be hidden for a particular page.
	 *
	 * @type {boolean | null}
	 * @memberof IFooter
	 */
	disableStickyFooterOnDesktop?: boolean | null;
}

export function BackToTopArrow() {
	return (
		<TAGLink href="#" title="Back to top" dataTestId="link_go_to_top_arrow" linkSx={backToTopLinkWrapperStyle}>
			<Box sx={backToTopBoxWrapperStyles(false)}>
				<ExpandLessIcon fontSize="small" color="inherit" />
				<Box sx={{ ...visuallyHidden }}>Back to </Box>
				<Typography variant="buttonLabel" fontSize="small" color="text.light">
					Top
				</Typography>
			</Box>
		</TAGLink>
	);
}

function StickyFooterButtonsCollection({ stickyFooterButtonsCollection }: Readonly<IStickyFooterButtons>) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const dataTestDeviceIdString = isSmallScreen ? 'mobile' : 'desktop';

	if (!stickyFooterButtonsCollection?.items.length) {
		return null;
	}

	return (
		<Box sx={stickyDesktopFooterBtnsWrapperStyles} data-testid={`sticky_footer_buttons_${dataTestDeviceIdString}`}>
			{stickyFooterButtonsCollection.items.map((button: Maybe<Button>) => {
				if (!button) {
					return null;
				}
				return (
					<ContentfulButton
						key={`sticky_footer_button_${dataTestDeviceIdString}_${button.label ?? ''}`}
						dataTestId={`sticky_footer_button_${dataTestDeviceIdString}_${button.label ?? ''}`}
						{...button}
						isSmallScreen={isSmallScreen}
					/>
				);
			})}
		</Box>
	);
}

function StickyDesktopFooter({ stickyFooterScheduleButton, stickyFooterButtonsCollection }: Readonly<IStickyFooter>) {
	const scheduleBtnLinkFromContentful = stickyFooterScheduleButton?.buttonLink ?? SCHEDULE_PATH;
	const [selectedState, setSelectedState] = useState<string>(scheduleBtnLinkFromContentful);
	const [scheduleBtnLink, setScheduleBtnLink] = useState<string>(stickyFooterScheduleButton?.buttonLink ?? '');
	const theme = useTheme();

	const handleStateChange = (event: SelectChangeEvent) => {
		const state = event.target.value;
		setSelectedState(state);
		const SELECTED_REASON = REASONS_FOR_VISIT.find((item) => item.value === state);
		const hasTrailingSlash = scheduleBtnLinkFromContentful.endsWith('/');
		const slashString = hasTrailingSlash ? '' : '/';
		const updatedScheduleBtnLink = SELECTED_REASON
			? `${scheduleBtnLinkFromContentful}${slashString}${SELECTED_REASON.path}/`
			: scheduleBtnLinkFromContentful;
		setScheduleBtnLink(updatedScheduleBtnLink);
	};

	const ITEM_HEIGHT = 48;
	const ITEM_PADDING_TOP = 8;
	const MenuProps = {
		PaperProps: {
			style: {
				maxHeight: ITEM_HEIGHT * 6.5 + ITEM_PADDING_TOP,
				width: 250,
			},
		},
		anchorOrigin: {
			vertical: 'top',
			horizontal: 'center',
		},
		transformOrigin: {
			vertical: 'bottom',
			horizontal: 'center',
		},
		style: {
			zIndex: 10_100,
		},
	} as MenuProps;

	return (
		<Box sx={stickyDesktopFooterWrapperStyles}>
			{stickyFooterScheduleButton && (
				<Box sx={stickyDesktopFooterBtnsWrapperStyles} data-testid="sticky_footer_schedule_button_desktop_ctr">
					<FormControl sx={getReasonSelectStyles(theme)}>
						<Select
							displayEmpty
							data-testid="sticky_footer_schedule_button_desktop_reason_select"
							value={selectedState ?? ''}
							onChange={handleStateChange}
							input={<OutlinedInput />}
							renderValue={(value) => {
								const item = REASONS_FOR_VISIT.find((item) => item.value === value);
								if (item) {
									return (
										<Box sx={reasonSelectedMenuItemStyles}>
											<TAGSvgIcon icon={item.icon} size={24} />
											<Typography variant="bodyMediumBook">{item.label}</Typography>
										</Box>
									);
								} else {
									return (
										<Box sx={reasonSelectedMenuItemStyles}>
											<Typography variant="bodyMediumBook">Reason for visit*</Typography>
										</Box>
									);
								}
							}}
							MenuProps={MenuProps}
							inputProps={{ 'aria-label': 'Without label' }}
						>
							{REASONS_FOR_VISIT.map((reason) => {
								return (
									<MenuItem
										value={reason.value}
										key={reason.value}
										sx={reasonMenuItemStyles}
										data-testid={reason.value}
									>
										<TAGSvgIcon icon={reason.icon} size={24} />
										<Typography variant="bodyMediumBook">{reason.label}</Typography>
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
					<ContentfulButton
						dataTestId="sticky_footer_schedule_button_desktop"
						{...stickyFooterScheduleButton}
						buttonLink={scheduleBtnLink}
					/>
				</Box>
			)}
			<StickyFooterButtonsCollection stickyFooterButtonsCollection={stickyFooterButtonsCollection} />
		</Box>
	);
}

function StickyMobileFooter({ stickyFooterScheduleButton, stickyFooterButtonsCollection }: Readonly<IStickyFooter>) {
	return (
		<Box sx={stickyMobileFooterWrapperStyles(!!stickyFooterButtonsCollection?.items?.length)}>
			{stickyFooterScheduleButton && (
				<ContentfulButton
					dataTestId="sticky_footer_schedule_button_mobile"
					{...stickyFooterScheduleButton}
					isSmallScreen
				/>
			)}
			<StickyFooterButtonsCollection stickyFooterButtonsCollection={stickyFooterButtonsCollection} />
		</Box>
	);
}

export default function Footer({
	footerData,
	withScheduling = false,
	breadcrumbsData,
	isLightFooter = false,
	disableStickyFooterOnMobile = false,
	disableStickyFooterOnDesktop = false,
}: Readonly<IFooter>) {
	const theme = useTheme();
	const isSmallDevice = useMediaQuery(theme.breakpoints.down('md'));

	return (
		<>
			<Box component="footer" data-testid="section_footer" sx={getWrapperStyles(isLightFooter)}>
				{!withScheduling && (
					<Grid container sx={breadCrumbsAndTopBtnContainerStyles}>
						{breadcrumbsData?.breadcrumbsCollection && (
							<Grid item sx={breadCrumbsContainerStyles}>
								<BreadCrumbs breadcrumbsData={breadcrumbsData} />
							</Grid>
						)}

						<BackToTopArrow />
					</Grid>
				)}

				<Container sx={getContentWrapperStyles} maxWidth={false}>
					<FooterNavigationContent
						isSmallDevice={isSmallDevice}
						withScheduling={withScheduling}
						navigationGroups={footerData?.navigationGroupsCollection}
						isLightFooter={isLightFooter}
					/>
					{withScheduling && !!footerData?.footerPolicyGroupCollection?.items?.length ? (
						<FooterPolicy policyGroup={footerData?.footerPolicyGroupCollection} />
					) : null}
					{!withScheduling && footerData?.contactNumber ? (
						<TAGLink
							href={`tel:${footerData.contactNumber || '#'}`}
							linkSx={getContactInfoWrapperStyles}
							dataTestId="link_contact_number"
							analyticsContext={{ clickPosition: 'footer' } as AnalyticsContext}
						>
							<Icon
								sx={{
									display: 'inline-flex',
									color: isLightFooter ? 'text.interactive' : 'text.light',
								}}
							>
								<PhoneIphoneIcon />
							</Icon>
							<Typography
								variant="header4"
								component={'span'}
								color={isLightFooter ? 'text.interactive' : 'text.light'}
								title="Contact Number"
							>
								{footerData.contactNumber}
							</Typography>
						</TAGLink>
					) : null}

					<Typography
						variant="bodyMediumBook"
						color={isLightFooter ? 'text.primary' : 'text.light'}
						sx={copyrightStyles}
						data-testid="text_footer_copyright"
					>
						{footerData?.copyright || ''}
					</Typography>
				</Container>
			</Box>

			{isSmallDevice && footerData?.useMobileStickyFooter && !disableStickyFooterOnMobile && (
				<StickyMobileFooter
					stickyFooterScheduleButton={footerData.stickyFooterScheduleButton}
					stickyFooterButtonsCollection={footerData.stickyFooterButtonsCollection}
				/>
			)}

			{!isSmallDevice && footerData?.useDesktopStickyFooter && !disableStickyFooterOnDesktop && (
				<StickyDesktopFooter
					stickyFooterScheduleButton={footerData.stickyFooterScheduleButton}
					stickyFooterButtonsCollection={footerData.stickyFooterButtonsCollection}
				/>
			)}
		</>
	);
}
